<template>
  <v-card>
    <v-card-title>
      <v-btn
        color="primary"
        @click="processSelectedRecipients"
        :disabled="loading || selectedRecipients.length === 0"
        small
        outline
      >
        <v-icon class="pr-1">payment</v-icon>Sacar Selecionados
      </v-btn>
      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Buscar"
        single-line
        hide-details
      ></v-text-field>
    </v-card-title>

    <v-data-table
      :headers="headers"
      :items="sortedRecipients"
      :search="search"
      :pagination.sync="pag"
      v-model="selectedRecipients"
      item-key="recipient.id"
      select-all
      disable-sort
      :loading="loading"
      loading-text="Carregando recebedores..."
    >
      <template v-slot:headers="props">
        <tr>
          <th>
            <v-checkbox
              :input-value="props.all"
              :indeterminate="props.indeterminate"
              primary
              hide-details
              @click.stop="toggleAll"
            ></v-checkbox>
          </th>
          <th
            v-for="header in props.headers"
            :key="header.text"
            :class="['column']"
          >
            {{ header.text }}
          </th>
        </tr>
      </template>

      <template v-slot:items="props">
        <tr :active="props.selected" @click="props.selected = !props.selected">
          <td>
            <v-checkbox
              :input-value="props.selected"
              primary
              hide-details
            ></v-checkbox>
          </td>
          <td class="text-xs-center">{{ props.item.recipient.id }}</td>
          <td class="text-xs-center">
            {{ props.item.recipient.default_bank_account.holder_name }}
          </td>
          <td class="text-xs-center">
            {{ props.item.recipient.document | formatDocument }}
          </td>
          <td class="text-xs-center">
            {{ props.item.balance.available_amount | formatCurrency }}
          </td>
        </tr>
      </template>

      <template v-slot:no-results>
        <v-alert :value="true" color="error" icon="warning">
          Sua busca por "{{ search }}" não encontrou resultados.
        </v-alert>
      </template>

      <template v-slot:no-data>
        <div v-if="loading" class="text-center py-4">
          <v-progress-linear
            indeterminate
            color="purple"
            height="2"
            width="10"
            class="mt-0 mb-2"
          />
          <p class="text-center text-muted">
            Carregando dados... isso pode levar até 5 minutos
          </p>
        </div>
        <v-alert v-else :value="true" color="info" icon="info">
          Nenhum recebedor encontrado.
        </v-alert>
      </template>
    </v-data-table>

    <!-- Dialog de confirmação -->
    <v-dialog v-model="dialogConfirm" max-width="500" dark>
      <v-card color="gray lighten-3">
        <v-card-title class="headline"> Confirmar processamento </v-card-title>
        <v-card-text>
          <div class="mb-3">
            Tem certeza que deseja processar
            <strong>{{ selectedRecipients.length }} recebedor(es)</strong>
            selecionado(s)?
          </div>

          <v-divider class="my-3"></v-divider>

          <div class="withdrawal-summary">
            <div class="d-flex justify-space-between mb-2">
              <span>Valor total dos saques:</span>
              <strong>{{ withdrawalSummary.total | formatCurrency }}</strong>
            </div>
            <div class="d-flex justify-space-between mb-2">
              <span
                >Taxa de saque ({{ selectedRecipients.length }} × R$
                3,67):</span
              >
              <strong>{{ withdrawalSummary.fee | formatCurrency }}</strong>
            </div>
            <v-divider class="my-2"></v-divider>
            <div class="d-flex justify-space-between">
              <span><strong>Total a ser processado:</strong></span>
              <strong class="primary--text">{{
                withdrawalSummary.totalWithFee | formatCurrency
              }}</strong>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="red"
            dark
            @click="dialogConfirm = false"
            :disabled="processingWithdrawal"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="green"
            dark
            @click="confirmProcessing"
            :loading="processingWithdrawal"
            :disabled="processingWithdrawal"
          >
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar :color="color" top v-model="snackbar" dark>
      <v-icon color="white" class="mr-3"> mdi-bell-plus </v-icon>
      <div>{{ snackMessage }}</div>
      <v-icon size="16" @click="snackbar = false"> mdi-close-circle </v-icon>
    </v-snackbar>
  </v-card>
</template>

<script>
import {
  getRecipientsWithBalance,
  processManualWithdraw,
} from "@/services/recipients";

export default {
  name: "Withdrawals",
  data() {
    return {
      search: "",
      selectedRecipients: [],
      dialogConfirm: false,
      loading: false,
      processingWithdrawal: false,
      recipients: [],
      allSelected: false,
      headers: [
        {
          text: "ID do Recebedor",
          align: "center",
          sortable: false,
          value: "recipient.id",
        },
        {
          text: "Nome da Conta Bancária",
          value: "recipient.default_bank_account.holder_name",
          align: "center",
          sortable: false,
        },
        {
          text: "Documento",
          value: "recipient.document",
          align: "center",
          sortable: false,
        },
        {
          text: "Valor Disponível",
          value: "balance.available_amount",
          align: "center",
          sortable: false,
        },
      ],
      pag: {
        rowsPerPage: 50,
      },
      color: "",
      snackMessage: "",
      snackbar: false,
    };
  },
  mounted() {
    this.getRecipients();
  },
  computed: {
    sortedRecipients() {
      if (!this.recipients) return [];

      return [...this.recipients].sort((a, b) => {
        const valueA = a.balance?.available_amount || 0;
        const valueB = b.balance?.available_amount || 0;
        return valueB - valueA;
      });
    },

    withdrawalSummary() {
      if (!this.selectedRecipients.length)
        return { total: 0, fee: 0, totalWithFee: 0 };

      const feePerRecipient = 367; // R$ 3,67 em centavos
      const totalAmount = this.selectedRecipients.reduce((sum, recipient) => {
        return sum + (recipient.balance?.available_amount || 0);
      }, 0);
      const totalFee = this.selectedRecipients.length * feePerRecipient;

      return {
        total: totalAmount,
        fee: totalFee,
        totalWithFee: totalAmount + totalFee,
      };
    },
  },
  filters: {
    formatDocument: (document) => {
      if (!document) return "-";

      // Remove caracteres não numéricos
      const cleanDoc = document.replace(/\D/g, "");

      // Formata CPF (11 dígitos)
      if (cleanDoc.length === 11) {
        return cleanDoc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      }

      // Formata CNPJ (14 dígitos)
      if (cleanDoc.length === 14) {
        return cleanDoc.replace(
          /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
          "$1.$2.$3/$4-$5",
        );
      }

      return document;
    },

    formatCurrency: (value) => {
      if (!value) return "R$ 0,00";

      const valueInReais = value / 100;

      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(valueInReais);
    },
  },
  methods: {
    async processWithdrawals(recipientsIds) {
      try {
        let payload = {};

        if (this.allSelected) {
          payload.process_all = true;
        } else {
          payload.recipient_ids = recipientsIds;
        }

        const response = await processManualWithdraw(payload);
        return response;
      } catch (error) {
        console.error("Erro ao processar saques:", error);
        throw error;
      }
    },

    async getRecipients() {
      try {
        this.loading = true;
        const response = await getRecipientsWithBalance();
        console.log("Recebedores carregados:", response);
        this.recipients = response.data?.filter((r) => r.balance) || [];
      } catch (error) {
        console.error("Erro ao carregar recebedores:", error);
        this.snack("Erro ao carregar recebedores. Tente novamente.", "error");
        this.recipients = [];
      } finally {
        this.loading = false;
      }
    },

    toggleAll() {
      if (this.selectedRecipients.length) {
        this.selectedRecipients = [];
        this.allSelected = false;
      } else {
        this.selectedRecipients = this.sortedRecipients.slice();
        this.allSelected = true;
      }
    },

    processSelectedRecipients() {
      if (this.selectedRecipients.length === 0) {
        this.snack("Selecione pelo menos um recebedor.", "warning");
        return;
      }

      this.dialogConfirm = true;
    },

    async confirmProcessing() {
      try {
        this.processingWithdrawal = true;

        const recipientIds = this.selectedRecipients.map(
          (recipient) => recipient.recipient.id,
        );

        await this.processWithdrawals(recipientIds);

        this.selectedRecipients.forEach((selectedRecipient) => {
          const recipientInList = this.recipients.find(
            (r) => r.recipient.id === selectedRecipient.recipient.id,
          );
          if (recipientInList && recipientInList.balance) {
            recipientInList.balance.available_amount = 0;
          }
        });

        this.snack(
          `${recipientIds.length} recebedor(es) processado(s) com sucesso!`,
          "success",
        );

        this.selectedRecipients = [];
        this.allSelected = false;
      } catch (error) {
        console.error("Erro ao processar recebedores:", error);
        this.snack("Erro ao processar recebedores. Tente novamente.", "error");
      } finally {
        this.processingWithdrawal = false;
        this.dialogConfirm = false;
      }
    },

    snack(message, color) {
      this.color = color;
      this.snackMessage = message;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.v-data-table tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.v-data-table tbody tr.v-data-table__selected {
  background: rgba(25, 118, 210, 0.12);
}

.withdrawal-summary {
  background-color: rgba(255, 255, 255, 0.1);
  padding: 16px;
  border-radius: 4px;
}
</style>
